import { Fragment, useEffect, useState } from "react";
import "./GitPage.css";
import { AppShell } from "../app/AppShell";
import { Button } from "../components/Button";
import { Alert, EmptyState, Loading } from "../components/Card";
import { PageHead } from "../components/Page";
import { Field, Input } from "../components/Field";
import { Chip } from "../components/Status";
import { GitConnSection } from "../components/GitConn";
import { RepoChecksChip, RepoChecksEditor } from "../components/RepoChecks";
import { RepoCreateForm } from "../components/RepoCreate";
import type { NewRepoInput } from "../components/RepoCreate";
import { connState, gitConnApi } from "../lib/gitconn";
import type { GitConn, GitConnList } from "../lib/gitconn";
import { authErrText } from "../lib/auth";
import { useRouteParam } from "../lib/route";
import { hole, rich, tx } from "../i18n";
import { EMPTY_FILTER, RepoFilterBar, filterRepos } from "../components/RepoFilter";
import type { RepoFilter } from "../components/RepoFilter";
import { projectApi } from "../lib/project";

/* ══ git 저장소 ══
   채택 시안 **안 C · 저장소가 주인공(연결은 접힌 띠)**, Figma node 46:2.
   문구와 판정표는 공통 프레임 47:2 에서 그대로 가져왔다.

   **왜 연결이 접혀 있나 — 시간 축이다.** 연결은 처음 한 번 넣고 끝인데 저장소는 계속
   붙였다 뗐다 한다. 주소·토큰 입력칸이 화면 절반을 차지하면 두 번째 방문부터는 매번
   안 쓰는 것을 보게 된다.

   **새 API 를 안 만든다** — 필요한 문은 이미 다 있다(공통 프레임 ③).
   토큰 값은 화면으로 안 내려온다. 화면이 아는 것은 「걸려 있나」뿐이다. */

const API = import.meta.env.VITE_B3XIN_API_URL ?? "/api";

type Conn = { baseUrl: string; tokenSet: boolean };
export type TestKind =
  | "ok"
  | "no-repos"
  | "unauthorized"
  | "not-found"
  | "http-error"
  | "unreachable"
  | "no-address";
type TestResult = { kind: TestKind; status?: number; ms?: number; repos: string[]; detail?: string };
/** `GET /repos` 가 주는 그대로. **필드를 줄여 적지 않는다** — 화면이 안 쓰는 값이라도
 *  이름을 맞춰 둬야 서버가 모양을 바꿨을 때 타입에서 드러난다. */
type Repo = {
  id: string;
  provider: string;
  owner: string;
  name: string;
  cloneUrl: string;
  defaultBranch: string | null;
  webUrl: string | null;
  taskCount: number | null;
  /** 어느 git 연결로 읽고 쓰나(git-hosts.md). `null` = 설치 기본 자격(gitea.json). */
  connectionId: string | null;
  /** 0030 — `null` = 설치 기본, `[]` = 요구 없음(`components/RepoChecks.tsx`). */
  requiredChecks: string[] | null;
  /** 0024 — 이 리포가 묶인 프로젝트들(둘 이상일 수 있다). */
  projectIds: string[];
};

/** `GET /repos/available` 의 한 줄 — 제공자에게 **지금 물어 본** 것이라 id 가 없다.
 *  `registered` 가 붙은 줄은 목록에서 빼지 않고 **못 고르게만** 한다(없어진 줄로 보이면
 *  사람이 혼란스럽다 — 서버 주석의 판단 그대로). */
type Avail = {
  provider: string;
  owner: string;
  name: string;
  cloneUrl: string;
  defaultBranch: string | null;
  webUrl: string | null;
  registered: boolean;
};

/** 화면에 적는 이름. 서버는 owner 와 name 을 따로 준다 — 붙이는 자리는 여기 하나다. */
function repoLabel(r: { owner: string; name: string }): string {
  return `${r.owner}/${r.name}`;
}

/** 시험 결과 일곱 갈래. **글리프 · 문구 · 색 셋이 늘 함께 간다** — 색만으로 안 가른다.
 *
 *  「저장소가 없음」은 **오류가 아니다**: 주소도 토큰도 맞고 갓 세운 Gitea 의 정상
 *  상태다. 빨갛게 칠하면 사람이 멀쩡한 주소부터 다시 본다(연결 설정의 `no-models` 와
 *  같은 판단, `crates/api/src/gitea.rs` 의 `verdict` 주석에도 같은 말이 있다).
 *
 *  「주소에 닿지 못함」과 「주소를 찾을 수 없음」은 **다른 갈래다** — 404 는 서버가 답을
 *  한 것이고 unreachable 은 서버를 못 찾은 것이라, 사람이 볼 데가 다르다. */
export const BANNER: Record<
  TestKind,
  { tone: "success" | "warning" | "error" | "info"; glyph: string; head: string; body: string }
> = {
  ok: { tone: "success", glyph: "✓", head: tx("conn.result.ok.head"), body: tx("git.result.ok") },
  "no-repos": { tone: "warning", glyph: "!", head: tx("git.result.noReposHead"), body: tx("git.result.noRepos") },
  unauthorized: { tone: "error", glyph: "✕", head: tx("conn.result.unauthorized.head"), body: tx("git.result.unauthorized") },
  "not-found": { tone: "error", glyph: "✕", head: tx("conn.result.notFound.head"), body: tx("git.result.notFound") },
  "http-error": { tone: "error", glyph: "✕", head: tx("conn.result.httpError.head"), body: tx("git.result.httpError") },
  unreachable: { tone: "error", glyph: "✕", head: tx("conn.result.unreachable.head"), body: tx("git.result.unreachable") },
  "no-address": { tone: "info", glyph: "·", head: tx("conn.result.noAddress.head"), body: tx("conn.result.noAddress.body") },
};

/** 「이슈 N건이 쓴다」. **빼기 전에 대가가 보여야 한다**(시안이 C 를 고른 이유).
 *
 *  `null` 은 **0 이 아니라 「모름」**이다 — 0 으로 적으면 「세어 봤더니 없다」와 구분이
 *  안 되고, 그 둘은 사람이 할 일이 정반대다(빼도 되는가 / 확인이 먼저인가). */
export function usageText(n: number | null | undefined): string {
  if (n === null || n === undefined) return tx("git.usage.unknown");
  return n === 0 ? tx("git.usage.none") : tx("git.usage.n", { n });
}

/** 패턴 페이지가 **이 순서대로** 일곱을 그린다. 화면과 같은 것을 읽어야 한 쪽만
 *  고쳐졌을 때 드러난다 — 목록을 거기서 다시 적으면 그 순간 두 벌이다. */
export const TEST_KINDS: TestKind[] = [
  "ok",
  "no-repos",
  "unauthorized",
  "not-found",
  "http-error",
  "unreachable",
  "no-address",
];

export function GitPage() {
  const [conn, setConn] = useState<Conn | null>(null);
  const [baseUrl, setBaseUrl] = useState("");
  const [token, setToken] = useState("");
  const [open, setOpen] = useState(false); // 연결 띠가 펼쳐졌나
  const [result, setResult] = useState<TestResult | null>(null);
  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [repos, setRepos] = useState<Repo[] | null>(null);
  const [picker, setPicker] = useState(false); // 「＋ 저장소 걸기」를 눌렀나
  const [avail, setAvail] = useState<Avail[] | null>(null);
  const [availErr, setAvailErr] = useState<string | null>(null);
  const [loadingAvail, setLoadingAvail] = useState(false);
  // 새 리포 만들기 — 실패 문장은 서버 원문, 성공은 한 줄. `createKey` 를 올려 폼을 비운다.
  const [creating, setCreating] = useState(false);
  const [createErr, setCreateErr] = useState<string | null>(null);
  const [createdName, setCreatedName] = useState<string | null>(null);
  const [createKey, setCreateKey] = useState(0);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [conns, setConns] = useState<GitConnList | null>(null);
  /** 고른 연결 — `""` 는 설치 기본(gitea.json). */
  const [pickConn, setPickConn] = useState<string>("");
  const [checksOpen, setChecksOpen] = useState<string | null>(null);
  const [filter, setFilter] = useState<RepoFilter>(EMPTY_FILTER);
  // 줄의 「소속 프로젝트」 이름표 — 곁가지라 못 받으면 id 로 적는다.
  const [projNames, setProjNames] = useState<Record<string, string>>({});
  const connected = useRouteParam("connected");
  const gitError = useRouteParam("git_error");

  function loadConns() {
    gitConnApi
      .list()
      .then(setConns)
      .catch((e) => setLoadErr(tx("gitConn.loadFailed", { err: authErrText(e) })));
  }
  useEffect(loadConns, []);
  useEffect(() => {
    projectApi.list().then((ps) => setProjNames(Object.fromEntries(ps.map((p) => [p.id, p.name]))), () => undefined);
  }, []);

  useEffect(() => {
    fetch(`${API}/admin/gitea/connection`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((c: Conn) => {
        setConn(c);
        setBaseUrl(c.baseUrl);
        // 2026-10-26: 연결 표(git-hosts.md)가 생긴 뒤로는 옛 폼을 펼쳐 두지 않는다 — 처음 온 사람의 첫 길은
        // 「연결 추가」(링크 승인)다. 옛 판단(「주소가 없으면 펼친다」)은 연결 표 이전의 것이다.
      })
      .catch((e: Error) => setLoadErr(e.message));
    fetch(`${API}/repos`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((rs: Repo[]) => setRepos(rs))
      .catch((e: Error) => setLoadErr(e.message));
  }, []);

  async function test() {
    setTesting(true);
    setResult(null);
    try {
      const r = await fetch(`${API}/admin/gitea/connection/test`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        /* **저장 없이 지금 칸의 값으로 시험한다.** 저장해야 시험이 되면, 안 되는 값을
           저장한 뒤라야 안 되는 줄 알게 된다. 토큰을 안 적었으면 서버가 저장된 것을
           쓴다 — 화면은 그 값을 모른다. */
        body: JSON.stringify({ baseUrl, token: token || undefined }),
      });
      setResult(await r.json());
    } catch (e) {
      setResult({ kind: "unreachable", repos: [], detail: (e as Error).message });
    } finally {
      setTesting(false);
    }
  }

  async function save() {
    setSaving(true);
    try {
      const r = await fetch(`${API}/admin/gitea/connection`, {
        method: "PUT",
        headers: { "content-type": "application/json" },
        /* 토큰은 **적었을 때만** 보낸다. 안 보내면 서버가 그대로 두는데, 그래야 주소만
           고치러 와서 저장했을 때 토큰이 안 날아간다. 빈 문자열은 「지움」이라 여기서
           만들면 안 된다 — 그 둘을 접지 마라. */
        body: JSON.stringify({ baseUrl, token: token ? token : undefined }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const c: Conn = await r.json();
      setConn(c);
      setBaseUrl(c.baseUrl);
      setToken(""); // 저장한 토큰은 화면에 안 남긴다.
      setOpen(false);
    } catch (e) {
      setResult({ kind: "unreachable", repos: [], detail: tx("settings.vendors.saveFailed", { err: (e as Error).message }) });
    } finally {
      setSaving(false);
    }
  }

  /** 목록은 **누를 때 부른다.** 상시로 두면 건 것과 안 건 것이 한 덩어리로 보여 지금
   *  무엇이 걸렸는지가 흐려진다(시안이 안 B 를 접은 이유). */
  async function openPicker() {
    const next = !picker;
    setPicker(next);
    if (!next) return;
    // 승인된 연결이 있으면 그것부터 — 설치 기본이 비어 있는 새 설치가 많다.
    const first = (conns?.connections ?? []).find((c) => connState(c) === "ok");
    const start = first && !conn?.baseUrl ? first.id : pickConn;
    setPickConn(start);
    await loadAvail(start);
  }

  /** 고른 연결로 호스트에 다시 묻는다. 승인 전 연결은 묻지 않는다 — 빈 목록으로 그리지 않는다. */
  async function loadAvail(connId: string) {
    setAvail(null);
    const c = conns?.connections.find((x) => x.id === connId);
    if (c && connState(c) !== "ok") {
      setAvailErr(null);
      return;
    }
    setLoadingAvail(true);
    setAvailErr(null);
    try {
      const r = await fetch(`${API}/admin/repos/available${connId ? `?connectionId=${encodeURIComponent(connId)}` : ""}`);
      const data = await r.json();
      if (!r.ok) {
        // **실패를 빈 목록으로 안 접는다** — 「리포가 없다」와 「못 읽었다」는 사람이 할
        // 일이 정반대다. 서버가 실은 이유를 그대로 보여준다(지어내지 않는다).
        throw new Error(data?.message ?? data?.detail ?? `HTTP ${r.status}`);
      }
      setAvail(data.repositories ?? []);
    } catch (e) {
      setAvail(null);
      setAvailErr((e as Error).message);
    } finally {
      setLoadingAvail(false);
    }
  }

  /** 고른 줄을 **그대로** 보낸다 — 화면이 값을 지어내지 않는다(clone 주소가 특히 그렇다). */
  async function attach(a: Avail) {
    try {
      const r = await fetch(`${API}/admin/repos`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          provider: a.provider,
          owner: a.owner,
          name: a.name,
          cloneUrl: a.cloneUrl,
          defaultBranch: a.defaultBranch ?? undefined,
          webUrl: a.webUrl ?? undefined,
          connectionId: pickConn || undefined,
        }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data?.message ?? `HTTP ${r.status}`);
      setRepos((v) => [...(v ?? []), data as Repo]);
      loadConns(); // 표의 리포 수
      setAvail((v) =>
        (v ?? []).map((x) => (x.owner === a.owner && x.name === a.name ? { ...x, registered: true } : x)),
      );
    } catch (e) {
      setLoadErr((e as Error).message);
    }
  }

  /** 호스트에 만들고 같은 요청에서 건다 — 받은 줄을 「건 저장소」와 고르기 목록 양쪽에 바로 얹는다. */
  async function createRepo(v: NewRepoInput) {
    setCreating(true);
    setCreateErr(null);
    setCreatedName(null);
    try {
      const r = await fetch(`${API}/admin/repos/create`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          connectionId: pickConn || undefined,
          owner: v.owner || undefined,
          name: v.name,
          private: v.private,
        }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data?.message ?? data?.detail ?? `HTTP ${r.status}`);
      const made = data as Repo;
      setRepos((x) => [...(x ?? []), made]);
      setAvail((x) => [
        ...(x ?? []),
        {
          provider: made.provider,
          owner: made.owner,
          name: made.name,
          cloneUrl: made.cloneUrl,
          defaultBranch: made.defaultBranch ?? null,
          webUrl: made.webUrl ?? null,
          registered: true,
        },
      ]);
      loadConns();
      setCreatedName(repoLabel(made));
      setCreateKey((k) => k + 1);
    } catch (e) {
      setCreateErr((e as Error).message);
    } finally {
      setCreating(false);
    }
  }

  async function detach(id: string) {
    try {
      const r = await fetch(`${API}/admin/repos/${encodeURIComponent(id)}`, { method: "DELETE" });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setRepos((v) => (v ?? []).filter((x) => x.id !== id));
    } catch (e) {
      setLoadErr((e as Error).message);
    }
  }

  const b = result ? BANNER[result.kind] : null;
  const attached = repos ?? [];
  const shown = filterRepos(attached, filter);
  const connList: GitConn[] = conns?.connections ?? [];
  const connLabel = (id: string | null) => (id ? connList.find((c) => c.id === id)?.label ?? id : tx("gitConn.pickLegacy"));
  const picked = connList.find((c) => c.id === pickConn);
  const pickedPending = picked && connState(picked) !== "ok";
  const canCreate = !pickedPending && !loadingAvail && !availErr && avail !== null && (pickConn === "" || picked?.provider === "gitea");

  async function approvePicked() {
    if (!picked) return;
    try {
      const r = await gitConnApi.authorize(picked.id);
      location.href = r.url;
    } catch (e) {
      setAvailErr(authErrText(e));
    }
  }

  return (
    <AppShell route="/git">
      <PageHead
        title={tx("nav.git")}
        lead={
          <>
            <p>{tx("git.lead1")}</p>
            <p>
              {rich(tx("git.lead2", hole("clone", "runner", "act")), {
                clone: <b>{tx("git.lead2Clone")}</b>,
                runner: <b>{tx("git.lead2Runner")}</b>,
                act: <code>act_runner</code>,
              })}
            </p>
          </>
        }
      />

      {loadErr ? <Alert tone="error">{tx("conn.loadFailed", { err: loadErr })}</Alert> : null}

      {/* ── 연결 표(시안 22:3) — 옛 gitea.json 은 맨 아래 「설치 기본」 줄, 「고치기」로 옛 폼을 펼친다 ── */}
      <GitConnSection
        data={conns}
        legacy={conn ? { baseUrl: conn.baseUrl, tokenSet: conn.tokenSet, repoCount: repos ? repos.filter((r) => !r.connectionId).length : null } : null}
        onLegacyFix={() => setOpen((v) => !v)}
        back={{ connected, error: gitError }}
        onChanged={loadConns}
        legacyForm={
          open ? (
            <div className="git__strip">
          <div className="git__form">
            <Field label={tx("conn.address")} hint={tx("git.addressHint")}>
              {(id) => (
                <Input
                  id={id}
                  value={baseUrl}
                  onChange={(e) => setBaseUrl(e.target.value)}
                  placeholder="http://gitea:3000"
                  inputMode="url"
                />
              )}
            </Field>
            <Field
              label={tx("git.token")}
              hint={tx(conn?.tokenSet ? "conn.tokenHintSet" : "git.tokenHintNone")}
            >
              {(id) => (
                <Input
                  id={id}
                  type="password"
                  value={token}
                  onChange={(e) => setToken(e.target.value)}
                  placeholder={tx(conn?.tokenSet ? "conn.tokenPhSet" : "conn.tokenPhNone")}
                  autoComplete="off"
                />
              )}
            </Field>
            <div className="git__actions">
              <Button variant="primary" onClick={test} loading={testing}>
                {tx("conn.test")}
              </Button>
              <Button onClick={save} loading={saving}>
                {tx("common.action.save")}
              </Button>
            </div>
          </div>
            </div>
          ) : null
        }
      />

      {b && result ? (
        <div className="git__banner">
          <Alert tone={b.tone}>
            <span className="git__glyph" aria-hidden="true">
              {b.glyph}
            </span>{" "}
            <b>{b.head}</b>
            {result.status ? ` (${result.status})` : ""} — {b.body}
            {result.kind === "ok" ? ` ${tx("git.okDetail", { n: result.repos.length, ms: result.ms ?? "?" })}` : ""}
            {result.detail ? ` [${result.detail}]` : ""}
          </Alert>
        </div>
      ) : null}

      {/* ── 이 설치에 건 저장소 — 주인공 ─────────────────────────────────── */}
      <section className="git__repos" aria-label={tx("git.attached")}>
        <div className="git__reposhead">
          <b>{tx("git.attached")}</b>
          <span className="muted">{tx("git.count", { n: attached.length })}</span>
          <span className="git__spacer" />
          <Button variant="primary" size="sm" onClick={openPicker} loading={loadingAvail}>
            ＋ {tx("git.attach")}
          </Button>
        </div>

        {attached.length ? (
          <RepoFilterBar rows={attached} value={filter} conns={connList.map((c) => ({ id: c.id, label: c.label }))} shown={shown.length} onChange={setFilter} />
        ) : null}
        {attached.length && !shown.length ? (
          <EmptyState title={tx("repoFilter.emptyTitle")} desc={tx("repoFilter.emptyDesc")} />
        ) : null}
        {attached.length ? (
          <>
            <ul className="git__list">
              {shown.map((r) => (
                <Fragment key={r.id}>
                <li className="git__row">
                  <span className="git__glyph git__glyph--ok" aria-hidden="true">
                    ■
                  </span>
                  <span className="git__rowtext">
                    <span className="git__reponame">{repoLabel(r)}</span>
                    <Chip>{connLabel(r.connectionId)}</Chip>
                    {r.projectIds.length ? (
                      r.projectIds.map((pid) => (
                        <a key={pid} className="git__proj" href={`#/projects?p=${encodeURIComponent(pid)}`}>
                          {projNames[pid] ?? pid}
                        </a>
                      ))
                    ) : (
                      <span className="muted">{tx("repoFilter.noProject")}</span>
                    )}
                    <span className="muted">{usageText(r.taskCount)}</span>
                    <RepoChecksChip
                      value={r.requiredChecks}
                      open={checksOpen === r.id}
                      onToggle={() => setChecksOpen(checksOpen === r.id ? null : r.id)}
                    />
                  </span>
                  <span className="git__spacer" />
                  <button type="button" className="b-btn b-btn-ghost b-btn-sm git__unlink" onClick={() => detach(r.id)}>
                    {tx("git.detach")}
                  </button>
                </li>
                {checksOpen === r.id ? (
                  <li>
                    <RepoChecksEditor
                      repoId={r.id}
                      value={r.requiredChecks}
                      onClose={() => setChecksOpen(null)}
                      onSaved={(v) => setRepos((xs) => (xs ?? []).map((x) => (x.id === r.id ? { ...x, requiredChecks: v } : x)))}
                    />
                  </li>
                ) : null}
                </Fragment>
              ))}
            </ul>
            {/* **빼기 전에 대가를 말한다.** 이 한 줄이 안 C 를 고른 이유다. */}
            <p className="git__warn">{tx("git.detachWarn")}</p>
          </>
        ) : (
          <EmptyState
            title={tx("git.emptyAttached")}
            desc={tx("git.emptyAttachedDesc")}
          />
        )}

        {/* **목록은 상시로 두지 않는다** — 건 것과 안 건 것이 한 덩어리로 보이면 지금
            무엇이 걸렸는지가 흐려진다(시안이 안 B 를 접은 이유). */}
        {picker ? (
          <div className="git__picker">
            {connList.length ? (
              <>
                <b>{tx("gitConn.pick")}</b>
                <div className="gconn__chips" role="radiogroup">
                  {connList.map((c) => (
                    <Button
                      key={c.id}
                      size="sm"
                      variant={pickConn === c.id ? "primary" : "secondary"}
                      aria-pressed={pickConn === c.id}
                      onClick={() => {
                        setPickConn(c.id);
                        loadAvail(c.id);
                      }}
                    >
                      {c.label}
                      {connState(c) === "ok" ? "" : ` ${tx(`gitConn.state.${connState(c)}`)}`}
                    </Button>
                  ))}
                  <Button
                    size="sm"
                    variant={pickConn === "" ? "primary" : "secondary"}
                    aria-pressed={pickConn === ""}
                    onClick={() => {
                      setPickConn("");
                      loadAvail("");
                    }}
                  >
                    {tx("gitConn.pickLegacy")}
                  </Button>
                </div>
              </>
            ) : null}
            {pickedPending && picked ? (
              <>
                <Alert tone="warning">{tx("gitConn.pickPending", { label: picked.label })}</Alert>
                <Button variant="primary" onClick={approvePicked}>
                  {tx("gitConn.pickApprove")}
                </Button>
              </>
            ) : loadingAvail ? (
              <Loading text={tx("git.asking")} />
            ) : availErr ? (
              /* **「없다」와 「못 읽었다」를 안 접는다** — 사람이 할 일이 정반대다. */
              <Alert tone="error">
                <span aria-hidden="true">✕</span>{" "}
                {rich(tx("git.availFailed", { err: availErr, ...hole("head") }), { head: <b>{tx("git.availFailedHead")}</b> })}
              </Alert>
            ) : !avail ? (
              <EmptyState
                title={tx("git.notConnected")}
                desc={tx("git.notConnectedDesc")}
              />
            ) : avail.length === 0 ? (
              <EmptyState
                title={tx("git.giteaEmpty")}
                desc={tx("git.giteaEmptyDesc")}
              />
            ) : (
              <ul className="git__list">
                {avail.map((a) => (
                  <li key={`${a.provider}/${a.owner}/${a.name}`} className="git__row">
                    <span
                      className={a.registered ? "git__glyph git__glyph--ok" : "git__glyph"}
                      aria-hidden="true"
                    >
                      {a.registered ? "■" : "□"}
                    </span>
                    <span className="git__rowtext">
                      <span className="git__reponame">{repoLabel(a)}</span>
                      <span className="muted">{tx(a.registered ? "git.alreadyOn" : "git.notYetOn")}</span>
                    </span>
                    <span className="git__spacer" />
                    {/* 이미 건 줄도 **목록에 남긴다** — 사라지면 사람이 「어디 갔지」를 한다. */}
                    <Button size="sm" disabled={a.registered} onClick={() => attach(a)}>
                      {tx(a.registered ? "git.on" : "git.put")}
                    </Button>
                  </li>
                ))}
              </ul>
            )}
            {/* 만들기는 Gitea 만 연다(코어). 승인 전 · 못 읽은 연결에서는 안 그린다 — 실패할 문을 내밀지 않는다. */}
            {canCreate ? (
              <>
                {createdName ? <p className="caption">✓ {tx("repoCreate.made", { name: createdName })}</p> : null}
                <RepoCreateForm key={createKey} busy={creating} error={createErr} onSubmit={createRepo} />
              </>
            ) : null}
          </div>
        ) : (
          <p className="caption">{tx("git.pickerHint")}</p>
        )}
      </section>

      <p className="caption">{rich(tx("git.projectsMoved", hole("link")), { link: <a href="#/projects">{tx("nav.projects")}</a> })}</p>
    </AppShell>
  );
}
